import { useQuery } from "@tanstack/react-query";
import { User } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { useToast } from "@/components/ui/use-toast";
import type { Tables } from "@/integrations/supabase/types";

interface PropertyExpertAnalysisProps {
  propertyId: string;
}

type ExpertAnalysisWithUser = Tables<"property_expert_analysis"> & {
  profiles: Pick<Tables<"profiles">, "first_name" | "last_name"> | null;
};

export const PropertyExpertAnalysis = ({ propertyId }: PropertyExpertAnalysisProps) => {
  const { toast } = useToast();

  const { data: analyses, isLoading } = useQuery({
    queryKey: ["property-expert-analysis", propertyId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("property_expert_analysis")
        .select(`
          *,
          profiles(
            first_name,
            last_name
          )
        `)
        .eq("property_id", propertyId)
        .eq("status", "active")
        .order("created_at", { ascending: false });

      if (error) {
        toast({
          title: "Error",
          description: "Could not load expert analyses",
          variant: "destructive",
        });
        throw error;
      }

      return data as unknown as ExpertAnalysisWithUser[];
    },
  });

  if (isLoading) {
    return <div className="animate-pulse h-48 bg-gray-100 rounded-lg" />;
  }

  if (!analyses?.length) {
    return null;
  }

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold">Expert Analysis</h2>
      <div className="space-y-4">
        {analyses?.map((analysis) => (
          <Card key={analysis.id} className="p-4">
            <div className="flex items-start space-x-3">
              <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                <User className="h-6 w-6 text-gray-400" />
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">
                      {analysis.profiles?.first_name} {analysis.profiles?.last_name}
                    </p>
                    {analysis.credentials && (
                      <p className="text-sm text-muted-foreground">
                        {JSON.stringify(analysis.credentials)}
                      </p>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {new Date(analysis.created_at || "").toLocaleDateString()}
                  </p>
                </div>
                <h3 className="font-medium mt-2">{analysis.title}</h3>
                <p className="text-muted-foreground mt-1">{analysis.content}</p>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};
